import * as React from "react"
import { FileCheck2, Loader2, ShieldAlert, Users } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDateTime } from "@/lib/date-time"
import { fetchLegalHistory, withdrawHrisPermission, type LegalHistory } from "@/features/integrations/hris/hrisApi"
import { HrisEmployeeNoticeDialog } from "./HrisEmployeeNoticeDialog"

type Props = {
  connectionId: number
  legalComplete: boolean
  onOpenActivation: () => void
  onRead: () => void
  /** Called after a withdrawal: the sync is paused and the HR copies deleted server-side. */
  onWithdrawn: () => void
  /** Bumped by the parent after an acceptance, to reload the record. */
  refreshKey: number
}

function person(p: { name: string; email: string | null } | null): string {
  if (!p) return "—"
  return p.email ? `${p.name} (${p.email})` : p.name
}

/**
 * Schedule E and the HR-data permission: whether they are in force, who
 * accepted and gave them and when, the full history (withdrawals included),
 * and the way out. Only those who may sign for the company see the record.
 */
export function HrisLegalCard({ connectionId, legalComplete, onOpenActivation, onRead, onWithdrawn, refreshKey }: Props) {
  const t = useBookingT()
  const [history, setHistory] = React.useState<LegalHistory | null | undefined>(undefined)
  const [confirm, setConfirm] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [noticeOpen, setNoticeOpen] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    fetchLegalHistory()
      .then((h) => !cancelled && setHistory(h))
      .catch(() => !cancelled && setHistory(null))
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const acceptance = history?.acceptances.find((a) => !a.superseded) ?? null
  const standing = history?.consents.find((c) => c.withdrawnAt === null) ?? null

  const withdraw = async () => {
    setBusy(true)
    try {
      await withdrawHrisPermission()
      toast.success(t("hris_withdrawn", "Permission withdrawn. The sync is paused and the HR data copies are deleted."))
      setConfirm(false)
      setHistory(await fetchLegalHistory())
      onWithdrawn()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start justify-between gap-3 max-md:flex-col">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-neutral-900">{t("hris_legal_title", "Agreement and permission")}</h2>
              {legalComplete ? (
                <Badge variant="green">{t("hris_legal_in_force", "In force")}</Badge>
              ) : (
                <Badge variant="warning">{t("hris_legal_missing", "Needed before switching on")}</Badge>
              )}
            </div>
            <p className="max-w-2xl text-sm text-neutral-800">
              {t(
                "hris_legal_intro",
                "Schedule E says which employee data Travel Code receives from your HR system and why. The permission is given separately and can be withdrawn at any time. Before the first sync, tell your employees: use the ready-made notice.",
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onRead}>
              <FileCheck2 className="size-4" />
              {t("hris_legal_read", "Read Schedule E")}
            </Button>
            <Button variant="outline" onClick={() => setNoticeOpen(true)}>
              <Users className="size-4" />
              {t("hris_notice_btn", "Notice to employees")}
            </Button>
            {!legalComplete && <Button onClick={onOpenActivation}>{t("hris_step_legal_btn", "Review and accept")}</Button>}
            {standing && (
              <Button variant="outline" className="text-error-700" onClick={() => setConfirm(true)}>
                {t("hris_legal_withdraw", "Withdraw permission")}
              </Button>
            )}
          </div>
        </div>

        {history === undefined && <Loader2 className="size-4 animate-spin text-brand-500" />}

        {history && (
          <dl className="grid grid-cols-1 gap-4 border-t border-neutral-200 pt-4 md:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-800">{t("hris_legal_schedule", "Schedule E")}</dt>
              <dd className="mt-1 text-sm text-neutral-900">
                {acceptance
                  ? t("hris_legal_accepted", "Accepted {date} by {who}, version {version}")
                      .replace("{date}", formatUserDateTime(acceptance.acceptedAt))
                      .replace("{who}", person(acceptance.by))
                      .replace("{version}", acceptance.versionLabel ?? "—")
                  : t("hris_legal_not_accepted", "Not accepted yet")}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-800">{t("hris_legal_permission", "Permission to receive employee data")}</dt>
              <dd className="mt-1 text-sm text-neutral-900">
                {standing
                  ? t("hris_legal_granted", "Given {date} by {who}")
                      .replace("{date}", formatUserDateTime(standing.grantedAt))
                      .replace("{who}", person(standing.grantedBy))
                  : t("hris_legal_not_granted", "Not given")}
              </dd>
            </div>
          </dl>
        )}

        {history && history.consents.length + history.acceptances.length > 1 && (
          <details className="text-sm text-neutral-800">
            <summary className="cursor-pointer text-neutral-900">{t("hris_legal_history", "Full history")}</summary>
            <ul className="mt-2 space-y-1">
              {history.acceptances.map((a, i) => (
                <li key={`a${i}`}>
                  {t("hris_legal_h_accepted", "{date}: Schedule E {version} accepted by {who}")
                    .replace("{date}", formatUserDateTime(a.acceptedAt))
                    .replace("{version}", a.versionLabel ?? "")
                    .replace("{who}", person(a.by))}
                </li>
              ))}
              {history.consents.map((c, i) => (
                <React.Fragment key={`c${i}`}>
                  <li>
                    {t("hris_legal_h_granted", "{date}: permission given by {who}")
                      .replace("{date}", formatUserDateTime(c.grantedAt))
                      .replace("{who}", person(c.grantedBy))}
                  </li>
                  {c.withdrawnAt && (
                    <li>
                      {t("hris_legal_h_withdrawn", "{date}: permission withdrawn by {who}")
                        .replace("{date}", formatUserDateTime(c.withdrawnAt))
                        .replace("{who}", person(c.withdrawnBy))}
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
          </details>
        )}
      </CardContent>

      <HrisEmployeeNoticeDialog connectionId={connectionId} open={noticeOpen} onOpenChange={setNoticeOpen} />

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="size-5 text-error-700" />
              {t("hris_withdraw_title", "Withdraw the permission?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "hris_withdraw_text",
                "The sync stops at once and the copies of HR data kept for it are deleted. Employees already in Travel Code stay. To sync again you will have to give the permission again.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>
              {t("hris_cancel", "Cancel")}
            </Button>
            <Button variant="destructive" disabled={busy} onClick={() => void withdraw()}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t("hris_legal_withdraw", "Withdraw permission")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
